import React from 'react';
import baseLogo from '../assets/partners/base.svg';
import daouDataLogo from '../assets/partners/daoudata.svg';
import danalLogo from '../assets/partners/danal.png';
import ethereumLogo from '../assets/partners/ethereum.png';
import fingerLogo from '../assets/partners/finger.png';
import imBankLogo from '../assets/partners/imbank.png';
import kaiaLogo from '../assets/partners/kaia.png';
import solanaLogo from '../assets/partners/solana.svg';

const mono: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace" };

type Partner = {
  name: string;
  logo: React.ReactNode;
};

const LOGO_HEIGHT = 26;
const partnerLogoTone: React.CSSProperties = { filter: 'grayscale(1) saturate(0) brightness(.68)', opacity: .82 };

const logoImage = (src: string, name: string, style?: React.CSSProperties) => (
  <img src={src} alt={`${name} logo`} style={{ display: 'block', width: 'auto', height: 'auto', maxWidth: '9.5rem', maxHeight: LOGO_HEIGHT, objectFit: 'contain', ...style }} />
);

const PARTNERS: Partner[] = [
  { name: 'Kaia', logo: logoImage(kaiaLogo, 'Kaia', { maxWidth: '6.8rem' }) },
  { name: 'Ethereum', logo: logoImage(ethereumLogo, 'Ethereum', { maxWidth: '8.6rem', maxHeight: 24 }) },
  { name: 'Base', logo: logoImage(baseLogo, 'Base', { maxWidth: '5.3rem' }) },
  { name: 'Solana', logo: logoImage(solanaLogo, 'Solana', { maxWidth: '8.8rem', maxHeight: 21 }) },
  { name: 'Finger', logo: logoImage(fingerLogo, 'Finger', { maxWidth: '6.4rem' }) },
  { name: 'Danal', logo: logoImage(danalLogo, 'Danal', { maxWidth: '7.5rem' }) },
  { name: 'Daou Data', logo: logoImage(daouDataLogo, 'Daou Data', { maxWidth: '11rem', maxHeight: 20 }) },
  { name: 'iM Bank', logo: logoImage(imBankLogo, 'iM Bank', { maxWidth: '7.4rem', maxHeight: 22 }) },
];

const PartnerItem: React.FC<{ partner: Partner; ariaHidden?: boolean }> = ({ partner, ariaHidden }) => (
  <div
    className="qssn-marquee-item"
    aria-hidden={ariaHidden || undefined}
    aria-label={ariaHidden ? undefined : partner.name}
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '16rem', minWidth: '16rem', height: '4.25rem', padding: '0 .75rem', boxSizing: 'border-box', whiteSpace: 'nowrap', ...partnerLogoTone }}
  >
    {partner.logo}
  </div>
);

type Props = { compact?: boolean };

const PartnerCarousel: React.FC<Props> = ({ compact = false }) => (
  <div style={{ background: 'transparent', borderTop: '1px solid var(--line)', padding: '3.25rem 0 3.5rem' }}>
    {compact ? (
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto 1.5rem',
          padding: '0 1.5rem',
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: 'center',
          gap: '.5rem 1.25rem',
        }}
      >
        <span style={{ ...mono, fontSize: '.6875rem', letterSpacing: '.18em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>
          {'Ecosystem & Partners'}
        </span>
      </div>
    ) : (
      <div style={{ maxWidth: 1200, margin: '0 auto 2.75rem', padding: '0 1.5rem' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem' }}>
          <span style={{ ...mono, fontSize: '.6875rem', letterSpacing: '.18em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>
            {'Ecosystem & Partners'}
          </span>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
            {['Networks', 'Payments', 'Banks', 'Integrators'].map((cat, i) => (
              <React.Fragment key={cat}>
                {i > 0 && <span style={{ color: 'var(--ink-3)' }}>·</span>}
                <span style={{ ...mono, fontSize: '.625rem', letterSpacing: '.12em', textTransform: 'uppercase', whiteSpace: 'nowrap', color: 'var(--ink-3)' }}>{cat}</span>
              </React.Fragment>
            ))}
          </div>
        </div>
        <p style={{ margin: '1.1rem 0 0', maxWidth: '44rem', fontSize: '1.25rem', lineHeight: 1.4, fontWeight: 500, letterSpacing: 0, color: 'var(--ink)', textWrap: 'balance' } as React.CSSProperties}>
          Built for the networks and institutions you already use.
        </p>
      </div>
    )}

    <div
      className="qssn-mwrap"
      style={{
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        overflowX: 'clip',
        WebkitMaskImage: 'linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent)',
        maskImage: 'linear-gradient(90deg,transparent,#000 8%,#000 92%,transparent)',
      }}
    >
      <div className="qssn-mtrack" style={{ display: 'flex', width: 'max-content', alignItems: 'center' }}>
        {PARTNERS.map((p) => (
          <PartnerItem key={`a-${p.name}`} partner={p} />
        ))}
        {PARTNERS.map((p) => (
          <PartnerItem key={`b-${p.name}`} partner={p} ariaHidden />
        ))}
      </div>
    </div>

    {!compact && (
      <p style={{ maxWidth: 1200, margin: '2.75rem auto 0', padding: '0 1.5rem', fontSize: '.8125rem', lineHeight: 1.5, color: 'var(--ink-3)' }}>
        Selected ecosystem and integration references. Third-party marks are shown for identification only.
      </p>
    )}
  </div>
);

export default PartnerCarousel;
